import { useState, useEffect } from "react";
import { useCameraInitialization } from "./useCameraInitialization";
import { useBarcodeDetection } from "./useBarcodeDetection";
import { useCameraFeeds } from "./useCameraFeeds";
import { type CameraConfig } from "../services/StorageService";

interface UseCameraSetupProps {
  cameraConfig: CameraConfig;
  onBarcodeDetected: (cameraIndex: number, barcode: string) => void;
  onBarcodeCleared: (cameraIndex: number) => void;
  validateBarcode?: (barcode: string) => boolean;
}

export const useCameraSetup = ({
  cameraConfig,
  onBarcodeDetected,
  onBarcodeCleared,
  validateBarcode = () => true,
}: UseCameraSetupProps) => {
  const [isInitializing, setIsInitializing] = useState(false);

  // Initialize camera hardware
  const {
    videoRef1,
    videoRef2,
    videoRef3,
    videoRefs,
    cameraInitializationState,
    availableCameras,
    initializeCameras,
    cleanupCameraStreams,
    handleVideoError,
  } = useCameraInitialization({
    cameraConfig,
  });

  // Manage camera feed state
  const {
    cameraFeeds,
    updateCameraStatus,
    clearCameraStatus,
    resetCameraFeeds,
  } = useCameraFeeds();

  // Set up barcode detection with per-slot enhancement configs
  const { isBarcodeApiSupported, initializeDetectors, stopAllDetection } =
    useBarcodeDetection(cameraInitializationState, videoRefs, {
      validateBarcode,
      onBarcodeDetected,
      onBarcodeCleared,
      slotConfigs: [cameraConfig.slot1, cameraConfig.slot2, cameraConfig.slot3], // Pass slot configs for enhancement
    });

  // Initialize cameras when component mounts or config changes
  useEffect(() => {
    const initCameras = async () => {
      setIsInitializing(true);
      try {
        // Use configured camera assignments, preserving slot positions
        const configuredDevices: (string | null)[] = [
          cameraConfig.slot1.deviceId || null,
          cameraConfig.slot2.deviceId || null,
          cameraConfig.slot3.deviceId || null,
        ];

        console.log("Using configured cameras with slot assignments:", configuredDevices);

        if (configuredDevices.some((deviceId) => deviceId !== null)) {
          await initializeCameras(configuredDevices);
        } else {
          console.warn(
            "No cameras configured. Please configure cameras in Manager Settings."
          );
        }
      } catch (err) {
        console.error("Error initializing configured cameras:", err);
      } finally {
        setIsInitializing(false);
      }
    };

    initCameras();

    return () => {
      cleanupCameraStreams();
      stopAllDetection();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraConfig]); // Only reinitialize when cameraConfig changes, not when the stable hook functions change

  // Check if cameras are configured
  const hasCamerasConfigured = !!(
    cameraConfig.slot1.deviceId ||
    cameraConfig.slot2.deviceId ||
    cameraConfig.slot3.deviceId
  );

  return {
    // Camera refs
    videoRef1,
    videoRef2,
    videoRef3,
    videoRefs,

    // Camera state
    cameraInitializationState,
    availableCameras,
    isInitializing,
    isBarcodeApiSupported,
    hasCamerasConfigured,

    // Camera feeds
    cameraFeeds,
    updateCameraStatus,
    clearCameraStatus,
    resetCameraFeeds,

    // Camera control
    initializeCameras,
    cleanupCameraStreams,
    handleVideoError,
    initializeDetectors,
    stopAllDetection,
  };
};
